import React, { useState, useEffect, memo } from 'react';
import {
    Button, Dialog, DialogContent, TextField, Typography,
    List, ListItem, ListItemText, CircularProgress, IconButton,
    Grid,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SaveIcon from '@mui/icons-material/Save';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { toast } from "react-hot-toast";
import configServ from '../../services/config';
import CheckAndReturn from '../../utility/CheckAndReturn';
import TitleDialog from '../Dialog/TitleDialog';
import { MultiSelect } from 'react-multi-select-component';
import Swal from 'sweetalert2';

const Questionnaires = ({ item }) => {
    //#region code
    const { user_id, company_id } = useSelector((state) => state.GlobalVariables);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const initialNewQuestionnaire = { title: '', description: '', status: true };
    const [questionnaires, setQuestionnaires] = useState([]);
    const [selectedQuestionnaires, setSelectedQuestionnaires] = useState([]);
    const [questionnairesList, setQuestionnairesList] = useState([]);
    const [validateError, setValidateError] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const fetchItemList = async () => {
        try {
            setLoading(true);
            const dataToSend = {
                company_id: company_id,
                item_id: item.id
            };
            const res = await configServ.getQuestionnairesBySubCategory(dataToSend);
            if (res.status === 200) {
                const result = res.data;
                const options = result.map((item) => ({
                    label: item.item_name,  // Assuming `fname` is the first name
                    value: item.id      // Assuming `id` is the customer ID
                }));
                setQuestionnaires(options);
            }
            else {
                setQuestionnaires([]);
            }
        }
        catch (e) {
            console.log(e);
        }
        finally {
            setLoading(false);
        }
    };

    // Effect to fetch questionnaires when the dialog is opened
    useEffect(() => {
        if (item && open) {
            fetchItemList();
            fetchQuestionnaires(); // Fetch existing questionnaires when the dialog opens
        }
    }, [item, open]);

    // Open the dialog
    const handleClickOpen = () => {
        setOpen(true);
    };

    // Close the dialog
    const handleClose = () => {
        clear();
        setOpen(false);
    };

    // Fetch questionnaires from the API
    const fetchQuestionnaires = async () => {
        setLoading(true);
        const abortController = new AbortController();

        try {
            const dataToSend = {
                item_id: item.id
            };
            const res = await configServ.getQuestionnairesByItemId(dataToSend, { signal: abortController.signal });

            if (res.status === 200) {
                const result = res.data.map(item => ({
                    id: item.id,
                    title: item.title,
                    description: item.description ?? null,
                    status: Boolean(item.status),
                }));
                setQuestionnairesList(result);
                const options = result.map((item) => ({
                    label: item.title,  // Assuming `fname` is the first name
                    value: item.id      // Assuming `id` is the customer ID
                }));
                setSelectedQuestionnaires(options);
                setIsEditing(true);
            } else {
                setQuestionnairesList([]);
                setSelectedQuestionnaires([]);
                setIsEditing(false);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error fetching questionnaires:', error);
            }
        } finally {
            setLoading(false);
        }

        return () => {
            abortController.abort(); // Clean up the API call if the component unmounts
        };
    };

    //validate form
    const validateForm = async () => {
        try {
            let errors = {};

            setValidateError(errors);
            return Object.values(errors).every(error => error === "");
        }
        catch (e) {
            console.log(e);
        }
    }

    // Save or update questionnaire
    const handleSubmit = async (e) => {
        e.preventDefault();
        const dataToSend = {
            user_id,
            item_id: item.id,
            questionnaires: selectedQuestionnaires,
        };
        try {
            const isValidate = await validateForm();
            if (isValidate === false) {
                return false;
            }
            let res;
            if (isEditing) {
                res = await configServ.updateQuestionnaires(dataToSend);
            } else {
                res = await configServ.createQuestionnaires(dataToSend);
            }

            if (res.status === 200) {
                clear();
                await fetchQuestionnaires(); // Refetch updated list
                handleClose();
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: res.message
                  });
            } 
            else {
                toast.error(res.message);
            }
        } catch (e) {
            console.log(e);
        }
    };

    const clear = () => {
        setQuestionnaires([]);
        setQuestionnairesList([]);
        setSelectedQuestionnaires([]);
        setValidateError({});
        setIsEditing(false);
    }
    //#endregion

    return (
        <>
            <Button fullWidth size='small' variant="contained" color="info" onClick={handleClickOpen} startIcon={<QuestionAnswerIcon />}>
                Questionnaires
            </Button>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" scroll='paper'>
                <TitleDialog title={'Questionnaires'} handleClose={handleClose} />
                <DialogContent
                    sx={{
                        minHeight: 600, // Minimum height for DialogContent
                    }}
                >
                    <form onSubmit={handleSubmit} onReset={clear}>
                        <Grid container sx={{ background: '#e3f3fc' }} py={1} px={2} borderRadius={2}>
                            <Grid container item justifyContent={'flex-start'}>
                                <Typography variant="body1" gutterBottom>
                                    <b>Pain Area: {`${item ? item.item_name : ''}`}</b>
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    Questionnaire
                                </Typography>
                                <MultiSelect
                                    options={questionnaires}
                                    value={selectedQuestionnaires}
                                    onChange={setSelectedQuestionnaires}
                                    labelledBy="Select"
                                    isLoading={loading}
                                />
                            </Grid>
                            <Grid container item xs={12} justifyContent={'flex-end'} my={1}>
                                <Button type='submit' title={isEditing ? "Update" : "Save"} color={isEditing ? "success" : "primary"} variant='contained' startIcon={<SaveIcon />} sx={{ paddingLeft: 4, mx: 1 }} />
                                <Button type='reset' color="secondary" variant='contained' startIcon={<RestartAltIcon />} sx={{ paddingLeft: 4, mx: 1 }} />
                            </Grid>
                        </Grid>
                    </form>
                    {loading ? (
                        <Grid container item alignItems={'center'} justifyContent="center">
                            <CircularProgress />
                        </Grid>
                    ) : (
                        <List>
                            {questionnairesList.length > 0 ? (
                                questionnairesList.map((q) => (
                                    <ListItem key={q.id} sx={{ backgroundColor: '#eeeef0', mb: 1, borderRadius: 2 }}>
                                        <ListItemText
                                            primary={q.title}
                                            secondary={q.description}
                                        />
                                    </ListItem>
                                ))
                            ) : (
                                <Grid container item justifyContent={'center'} alignItems={'center'}>
                                    <Typography variant='h6' color={'error'}>No questionnaire found.</Typography>
                                </Grid>
                            )}
                        </List>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default memo(Questionnaires);
